import { PublicIdSchemas } from '@idc-forms/id'
import { z } from 'zod'
import { requiredString } from '../primitives/string'
import { InstantSchema } from '../primitives/time'
import { V2CursorPaginationSchema } from './http'

/**
 * Contrato de la administración v2 de profesiones. Noveno catálogo del backoffice
 * en v2 y el más simple de la ola: es PLANO —no cuelga de nada—, así que su
 * listado no joinea ninguna colección. Lo único que resuelve por lote es la
 * autoría (`createdBy`/`updatedBy` → `usr_`), igual que empresas y representantes.
 *
 * `name` es único GLOBAL entre profesiones vivas (case-insensitive); un duplicado
 * responde `400 ERROR_PROFESSION_EXIST`.
 *
 * La fila especial «Otro» (`isOther`) es el fallback de texto libre de los
 * formularios KYC: la administra el backend y el CRUD no la toca. Sale en el wire
 * de salida —la UI necesita saber que esa fila no se edita ni se borra— y en el
 * lookup —los formularios la usan para ofrecer el campo libre—, pero NUNCA entra
 * al wire de entrada: si viajara, cualquiera podría fabricar una segunda «Otro» o
 * quitarle la marca a la real y romper el fallback de los formularios.
 *
 * El `name` viaja NORMALIZADO a MAYÚSCULAS. Alimenta los PDFs de FEIC y promesa a
 * través de la profesión del cliente y del representante legal —donde el contrato
 * v2 ya lo exige—, y hasta hoy la uniformidad dependía de quien tipeara: las 69
 * filas de la colección están en mayúsculas por costumbre, no por regla.
 */

/**
 * Autoría wire-safe: el `_id` del usuario no cruza el adapter, así que se publica su
 * `usr_`. Va entera nullable porque el legacy guarda `createdBy: null` en las filas
 * sembradas, y la UI hace mucho que resuelve ese caso a «—».
 */
export const V2AdminProfessionAuthorSchema = z
  .object({
    id: PublicIdSchemas.user,
    fullname: z.string()
  })
  .nullable()

/** Fila del listado y del detalle. */
export const V2AdminProfessionSchema = z.object({
  id: PublicIdSchemas.profession,
  name: z.string(),
  active: z.boolean(),
  /**
   * De sistema: marca la fila «Otro», el fallback de texto libre de los formularios
   * KYC. No se crea, no se edita y no se borra. Sólo lectura.
   */
  isOther: z.boolean(),
  createdBy: V2AdminProfessionAuthorSchema,
  updatedBy: V2AdminProfessionAuthorSchema,
  createdAt: InstantSchema,
  updatedAt: InstantSchema
})

/** Página cursor-paginada del listado (ADR 0019). */
export const V2AdminProfessionsPageSchema = z.object({
  data: z.array(V2AdminProfessionSchema),
  pagination: V2CursorPaginationSchema
})

/**
 * Total y faceta `active`, con los mismos filtros allowlist del listado. La faceta
 * ignora el filtro de su propia dimensión; `total` respeta la consulta completa.
 * `isOther` NO es faceta: cuenta exactamente una fila, así que un conteo por valor
 * no aporta navegación.
 */
export const V2AdminProfessionsSummarySchema = z.object({
  total: z.number().int().nonnegative(),
  active: z.object({
    true: z.number().int().nonnegative(),
    false: z.number().int().nonnegative()
  })
})

/**
 * Entrada mínima del selector de profesiones (formularios KYC de cliente y
 * representante). `isOther` viaja porque el formulario necesita distinguir la fila
 * que habilita el campo de texto libre.
 */
export const V2AdminProfessionLookupItemSchema = z.object({
  id: PublicIdSchemas.profession,
  name: z.string(),
  isOther: z.boolean()
})

export const V2AdminProfessionLookupSchema = z.object({
  data: z.array(V2AdminProfessionLookupItemSchema)
})

/**
 * Alta de profesión. `isOther` NO se acepta: la fila «Otro» la administra el
 * backend. El nombre se recorta y sube a MAYÚSCULAS, y debe ser único global entre
 * las vivas (`400 ERROR_PROFESSION_EXIST`).
 */
export const V2CreateAdminProfessionSchema = z.object({
  name: requiredString()
    .transform((value) => value.trim().toUpperCase())
    .pipe(requiredString()),
  active: z.boolean().optional()
})

/**
 * Edición parcial: un id desconocido responde 404 (nunca hace upsert, a diferencia
 * del `findOneAndUpdate` con `upsert` del legacy). La fila «Otro» no se puede
 * editar (`400 ERROR_PROFESSION_NOT_UPDATE_OTHER`).
 */
export const V2UpdateAdminProfessionSchema = V2CreateAdminProfessionSchema.partial()

export type V2AdminProfessionAuthor = z.infer<typeof V2AdminProfessionAuthorSchema>
export type V2AdminProfession = z.infer<typeof V2AdminProfessionSchema>
export type V2AdminProfessionsPage = z.infer<typeof V2AdminProfessionsPageSchema>
export type V2AdminProfessionsSummary = z.infer<typeof V2AdminProfessionsSummarySchema>
export type V2AdminProfessionLookupItem = z.infer<typeof V2AdminProfessionLookupItemSchema>
export type V2AdminProfessionLookup = z.infer<typeof V2AdminProfessionLookupSchema>
export type V2CreateAdminProfessionInput = z.infer<typeof V2CreateAdminProfessionSchema>
export type V2UpdateAdminProfessionInput = z.infer<typeof V2UpdateAdminProfessionSchema>
